import React, { useState } from 'react';
import { Send, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const ContactForm = ({ showHeading = true, className = '' }) => {
  const [formData, setFormData] = useState({
    contact: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [validationErrors, setValidationErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    if (submitStatus === 'error') {
      setSubmitStatus(null);
      setErrorMessage('');
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.contact.trim()) {
      errors.contact = 'Contact information is required';
    } else if (formData.contact.trim().length < 3) {
      errors.contact = 'Contact information must be at least 3 characters';
    } else if (formData.contact.trim().length > 200) {
      errors.contact = 'Contact information must be less than 200 characters';
    }

    if (!formData.message.trim()) {
      errors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      errors.message = 'Message must be at least 10 characters';
    } else if (formData.message.trim().length > 1000) {
      errors.message = 'Message must be less than 1000 characters';
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);
    setErrorMessage('');
    setValidationErrors({});

    try {
      const response = await axios.post(`${API_BASE_URL}/api/contact`, {
        contact: formData.contact.trim(),
        message: formData.message.trim()
      }, {
        headers: {
          'Content-Type': 'application/json'
        },
        timeout: 10000
      });

      if (response.data.success) {
        setSubmitStatus('success');
        setFormData({ contact: '', message: '' });
      } else {
        setSubmitStatus('error');
        setErrorMessage('Failed to send message. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');

      if (error.code === 'ECONNABORTED') {
        setErrorMessage('Request timed out. Please check your connection and try again.');
      } else if (error.response) {
        const status = error.response.status;
        const data = error.response.data;

        if (status === 429) {
          setErrorMessage('Too many requests. Please wait a moment before trying again.');
        } else if (status === 400 && data.errors) {
          const backendErrors = {};
          data.errors.forEach(err => {
            if (err.path === 'contact') {
              backendErrors.contact = err.msg;
            } else if (err.path === 'message') {
              backendErrors.message = err.msg;
            }
          });
          setValidationErrors(backendErrors);
        } else if (data.message) {
          setErrorMessage(data.message);
        } else {
          setErrorMessage(`Server error (${status}). Please try again later.`);
        }
      } else if (error.request) {
        setErrorMessage('Network error. Please check your connection and try again.');
      } else {
        setErrorMessage('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={className}>
      {showHeading && (
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            <span className="bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">
              Ready to Get Started?
            </span>
          </h2>
          <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
            Tell us about your project and we'll take it from there.
          </p>
        </div>
      )}

      <div className="max-w-2xl mx-auto">
        {submitStatus === 'success' && (
          <div className="flex items-center space-x-2 text-green-400 bg-green-400/10 p-4 rounded-lg mb-6">
            <CheckCircle className="h-5 w-5" />
            <span>Message sent successfully! We'll get back to you soon.</span>
          </div>
        )}

        {submitStatus === 'error' && (
          <div className="flex items-center space-x-2 text-red-400 bg-red-400/10 p-4 rounded-lg mb-6">
            <AlertCircle className="h-5 w-5" />
            <span>{errorMessage || 'Failed to send message. Please try again later.'}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="contact" className="block text-sm font-medium text-slate-300 mb-2">
              Contact
            </label>
            <input
              type="text"
              id="contact"
              name="contact"
              value={formData.contact}
              onChange={handleInputChange}
              required
              minLength={3}
              maxLength={200}
              className={`w-full px-4 py-3 bg-slate-800/50 border rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:border-transparent transition-colors ${
                validationErrors.contact
                  ? 'border-red-500 focus:ring-red-500'
                  : 'border-slate-600 focus:ring-indigo-500'
              }`}
              placeholder="Email address, Telegram, ..."
              aria-describedby={validationErrors.contact ? "contact-error" : undefined}
            />
            {validationErrors.contact && (
              <p id="contact-error" className="mt-1 text-sm text-red-400" role="alert">
                {validationErrors.contact}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="message" className="block text-sm font-medium text-slate-300 mb-2">
              Message
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              required
              minLength={10}
              maxLength={1000}
              rows={5}
              className={`w-full px-4 py-3 bg-slate-800/50 border rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:border-transparent resize-vertical transition-colors ${
                validationErrors.message
                  ? 'border-red-500 focus:ring-red-500'
                  : 'border-slate-600 focus:ring-indigo-500'
              }`}
              placeholder="Tell us about your project or how we can help you..."
              aria-describedby={validationErrors.message ? "message-error" : undefined}
            />
            {validationErrors.message && (
              <p id="message-error" className="mt-1 text-sm text-red-400" role="alert">
                {validationErrors.message}
              </p>
            )}
            <div className="mt-1 text-xs text-slate-400 text-right">
              {formData.message.length}/1000 characters
            </div>
          </div>

          <button
            type="submit"
            disabled={isSubmitting || !formData.contact.trim() || !formData.message.trim()}
            className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-slate-900"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                <span>Sending...</span>
              </>
            ) : (
              <>
                <Send className="h-5 w-5" />
                <span>Send Message</span>
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ContactForm;
